import { StrokePath, DISTANCE_SAMPLE_SPACING } from '../utils/StrokePath';

import type { PathProjection } from '../utils/StrokePath';

export type Point = {
	x: number;
	y: number;
};

export type DirectionalCheckpoint = {
	distanceFromPrevious: number;
	point: Point;
	tangent: Point;
};

export type CheckpointCrossing = {
	checkpointIndex: number;
	distance: number;
	movementProgress: number;
	point: Point;
	score: number;
};

export type CheckpointTraceResult = {
	completed: boolean;
	crossings: CheckpointCrossing[];
	completion?: { movementProgress: number; point: Point; endpoint: Point };
};

type TraceSegmentOptions = {
	maxMovementProgress?: number;
	endpointOnly?: boolean;
	corridorIntervals?: readonly { start: number; end: number }[];
};

export const FINISH_RADIUS_RATIO = 0.5;
const PROGRESS_EPSILON = 1e-7;
export const CHECKPOINT_SCORE_BAND_COUNT = 3;
export const SCORE_CORRIDOR_EXIT_PENALTY = -2;
const PERFECT_SCORE_DISTANCE_RATIO = 0.25;
const SCORE_BAND_BOUNDARY_EPSILON = 1e-10;

type CheckpointScore = {
	status: 'pending' | 'scored' | 'skipped';
	score: number;
};

const getScoreBandMetrics = (maxDistance: number, scoreBandCount: number) => {
	const perfectDistance = maxDistance * PERFECT_SCORE_DISTANCE_RATIO;

	return {
		perfectDistance,
		imperfectBandWidth: (maxDistance - perfectDistance) / (scoreBandCount - 1),
	};
};

export const getCheckpointScoreBandBoundaries = (maxDistance: number, scoreBandCount = CHECKPOINT_SCORE_BAND_COUNT) => {
	const { perfectDistance, imperfectBandWidth } = getScoreBandMetrics(maxDistance, scoreBandCount);

	return Array.from({ length: scoreBandCount - 1 }, (_unused, index) => perfectDistance + imperfectBandWidth * index);
};

export class StrokeEvaluator {
	private inactiveThrough: number;
	private readonly firstScoringCheckpoint: number;
	private readonly scoringCheckpointEnd: number;
	private readonly finishRadius: number;
	private corridorExitCount = 0;
	private readonly checkpointScores: CheckpointScore[];
	private readonly checkpointDistances: number[];
	private readonly trackingPath: StrokePath;
	private projection: PathProjection;
	private outside = false;
	private completed = false;

	constructor(
		private readonly checkpoints: DirectionalCheckpoint[],
		private readonly maxDistance: number,
		private readonly scoreBandCount = CHECKPOINT_SCORE_BAND_COUNT,
		trackingPath?: StrokePath,
	) {
		if (checkpoints.length === 0) {
			throw new Error('A tracing stroke must contain at least one checkpoint');
		}

		if (maxDistance <= 0) {
			throw new Error('Checkpoint max distance must be greater than zero');
		}

		if (scoreBandCount < 2) {
			throw new Error('Checkpoint score band count must be at least two');
		}

		if (checkpoints.slice(1).some(({ distanceFromPrevious }) => distanceFromPrevious <= 0)) {
			throw new Error('Checkpoint distance from previous checkpoint must be greater than zero');
		}

		this.finishRadius = maxDistance * FINISH_RADIUS_RATIO;
		const withinRadius = (point: Point, center: Point) =>
			Math.hypot(point.x - center.x, point.y - center.y) <= this.finishRadius + PROGRESS_EPSILON;
		let first = 0;
		let end = checkpoints.length;
		while (first < end && withinRadius(checkpoints[first].point, checkpoints[0].point)) first++;
		while (end > first && withinRadius(checkpoints[end - 1].point, checkpoints.at(-1)!.point)) end--;
		this.firstScoringCheckpoint = first;
		this.scoringCheckpointEnd = end;
		this.inactiveThrough = first - 1;

		let distance = 0;
		this.checkpointDistances = checkpoints.map((checkpoint, index) => {
			if (index > 0) distance += checkpoint.distanceFromPrevious;
			return distance;
		});
		this.trackingPath =
			trackingPath ??
			new StrokePath(
				checkpoints.map(({ point }, index) => ({
					point,
					distance: this.checkpointDistances[index],
				})),
			);
		this.projection = this.trackingPath.project(checkpoints[0].point, 0);
		this.checkpointScores = checkpoints.map((_checkpoint, index) => this.initialCheckpointScore(index));
	}

	getMaximumScore() {
		return this.scoringCheckpointEnd - this.firstScoringCheckpoint;
	}

	isScoringCheckpoint(index: number) {
		return index >= this.firstScoringCheckpoint && index < this.scoringCheckpointEnd;
	}

	getInactiveThrough() {
		return this.inactiveThrough;
	}

	getCheckpointScore(checkpointIndex: number) {
		return this.checkpointScores[checkpointIndex];
	}

	getScore() {
		return (
			this.checkpointScores.reduce((total, { score }) => total + score, 0) +
			this.corridorExitCount * SCORE_CORRIDOR_EXIT_PENALTY
		);
	}

	traceSegment(from: Point, to: Point, options: TraceSegmentOptions = {}): CheckpointTraceResult {
		if (this.completed) return { completed: true, crossings: [] };
		const { maxMovementProgress = 1, corridorIntervals = [{ start: 0, end: 1 }], endpointOnly = false } = options;
		const finishProgress = this.getFinishProgress(from, to, maxMovementProgress);
		if (endpointOnly && finishProgress === null) return { completed: false, crossings: [] };
		const acceptedProgress = finishProgress ?? maxMovementProgress;
		const pointAt = (progress: number) => ({
			x: from.x + (to.x - from.x) * progress,
			y: from.y + (to.y - from.y) * progress,
		});
		const movementLength = Math.hypot(to.x - from.x, to.y - from.y);
		const crossings: CheckpointCrossing[] = [];
		for (const interval of corridorIntervals) {
			if (interval.start > acceptedProgress) break;
			const start = interval.start;
			const end = Math.min(interval.end, acceptedProgress);
			if (this.outside || start > 0) this.recoverAt(pointAt(start));
			this.outside = false;
			const steps = Math.max(1, Math.ceil((movementLength * (end - start)) / DISTANCE_SAMPLE_SPACING));
			for (let step = 0; step < steps; step++) {
				const a = start + ((end - start) * step) / steps;
				const b = start + ((end - start) * (step + 1)) / steps;
				this.evaluateInside(pointAt(a), pointAt(b), a, b, crossings);
			}
			if (interval.end < acceptedProgress && movementLength > 0) {
				this.corridorExitCount++;
				this.outside = true;
			}
		}
		if (!corridorIntervals.some(({ start, end }) => start <= acceptedProgress && end >= acceptedProgress)) {
			this.outside = true;
		}
		if (finishProgress === null) return { completed: false, crossings };

		const finalIndex = this.checkpoints.length - 1;
		while (this.inactiveThrough < finalIndex) this.skipNext();
		const endpoint = this.checkpoints[finalIndex].point;
		this.completed = true;

		return {
			completed: true,
			crossings,
			completion: { movementProgress: finishProgress, point: pointAt(finishProgress), endpoint },
		};
	}

	reset() {
		this.inactiveThrough = this.firstScoringCheckpoint - 1;
		this.corridorExitCount = 0;
		this.outside = false;
		this.completed = false;
		this.projection = this.trackingPath.project(this.checkpoints[0].point, 0);
		this.checkpointScores.forEach((_score, index) => {
			this.checkpointScores[index] = this.initialCheckpointScore(index);
		});
	}

	private initialCheckpointScore(index: number): CheckpointScore {
		return { score: 0, status: this.isScoringCheckpoint(index) ? 'pending' : 'skipped' };
	}

	private hasPassedCheckpoint(distance: number, index: number, point?: Point) {
		if (distance <= this.checkpointDistances[index] + PROGRESS_EPSILON) return false;
		if (!point) return true;
		const checkpoint = this.checkpoints[index];
		const forwardOffset =
			(point.x - checkpoint.point.x) * checkpoint.tangent.x +
			(point.y - checkpoint.point.y) * checkpoint.tangent.y;
		// At a corner, projection can pass the checkpoint before the cursor crosses its bisected gate.
		return forwardOffset > PROGRESS_EPSILON || distance > this.checkpointDistances[index + 1] + PROGRESS_EPSILON;
	}

	private skipPassedCheckpoints(distance: number, point?: Point) {
		while (
			this.inactiveThrough < this.scoringCheckpointEnd - 1 &&
			this.hasPassedCheckpoint(distance, this.inactiveThrough + 1, point)
		)
			this.skipNext();
	}

	private skipNext() {
		this.checkpointScores[++this.inactiveThrough] = { score: 0, status: 'skipped' };
	}

	private recoverAt(point: Point) {
		let nearest = this.inactiveThrough + 1;
		if (nearest >= this.scoringCheckpointEnd) return;
		let distance = Infinity;
		for (let index = nearest; index < this.scoringCheckpointEnd; index++) {
			const candidate = this.checkpoints[index].point;
			const offset = Math.hypot(point.x - candidate.x, point.y - candidate.y);
			if (offset < distance - PROGRESS_EPSILON) {
				nearest = index;
				distance = offset;
			}
		}
		while (this.inactiveThrough < nearest - 1) this.skipNext();
		this.projection = this.trackingPath.project(
			point,
			this.trackingPath.segmentAt(this.checkpointDistances[nearest]),
		);
		this.skipPassedCheckpoints(this.projection.distance);
	}

	private evaluateInside(from: Point, to: Point, start: number, end: number, crossings: CheckpointCrossing[]) {
		const previous = this.trackingPath.project(from, this.projection.segmentIndex);
		const nextIndex = Math.min(this.inactiveThrough + 1, this.checkpoints.length - 1);
		const localSpacing = this.checkpoints[Math.max(1, nextIndex)]?.distanceFromPrevious ?? DISTANCE_SAMPLE_SPACING;
		this.projection = this.trackingPath.project(
			to,
			previous.segmentIndex,
			Math.hypot(to.x - from.x, to.y - from.y) + Math.max(DISTANCE_SAMPLE_SPACING, localSpacing),
			{ x: to.x - from.x, y: to.y - from.y },
		);
		this.skipPassedCheckpoints(previous.distance, from);
		let movementProgress = 0;
		while (this.inactiveThrough < this.scoringCheckpointEnd - 1) {
			const index = this.inactiveThrough + 1;
			const crossing = this.getForwardCrossing(from, to, index);
			const previousDistance = this.checkpointDistances[index - 1];
			if (
				crossing &&
				crossing.movementProgress >= movementProgress &&
				Math.max(previous.distance, this.projection.distance) >= previousDistance - PROGRESS_EPSILON
			) {
				this.inactiveThrough = index;
				this.checkpointScores[index] = { score: crossing.score, status: 'scored' };
				movementProgress = crossing.movementProgress;
				crossings.push({ ...crossing, movementProgress: start + (end - start) * crossing.movementProgress });
			} else if (this.hasPassedCheckpoint(this.projection.distance, index, to)) {
				this.skipNext();
				movementProgress = 1;
			} else break;
		}
	}

	private getFinishProgress(from: Point, to: Point, limit: number): number | null {
		const endpoint = this.checkpoints.at(-1)!.point;
		const ox = from.x - endpoint.x;
		const oy = from.y - endpoint.y;
		const c = ox * ox + oy * oy - this.finishRadius * this.finishRadius;
		if (c <= 0) return 0;
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		const a = dx * dx + dy * dy;
		if (a === 0) return null;
		const b = ox * dx + oy * dy;
		const discriminant = b * b - a * c;
		if (discriminant < 0) return null;
		const progress = (-b - Math.sqrt(discriminant)) / a;
		return progress >= 0 && progress <= limit + PROGRESS_EPSILON ? Math.min(progress, limit) : null;
	}

	private getForwardCrossing(from: Point, to: Point, checkpointIndex: number): CheckpointCrossing | null {
		const checkpoint = this.checkpoints[checkpointIndex];
		const tangentLength = Math.hypot(checkpoint.tangent.x, checkpoint.tangent.y);

		if (tangentLength === 0) {
			throw new Error(`Checkpoint ${checkpointIndex} has no forward tangent`);
		}

		const tangentX = checkpoint.tangent.x / tangentLength;
		const tangentY = checkpoint.tangent.y / tangentLength;
		// Dot each cursor offset with the tangent to determine which side of the checkpoint gate it is on.
		const fromOffset = (from.x - checkpoint.point.x) * tangentX + (from.y - checkpoint.point.y) * tangentY;
		const toOffset = (to.x - checkpoint.point.x) * tangentX + (to.y - checkpoint.point.y) * tangentY;

		const fromDistance = Math.abs(fromOffset) <= PROGRESS_EPSILON ? 0 : fromOffset;
		const toDistance = Math.abs(toOffset) <= PROGRESS_EPSILON ? 0 : toOffset;

		if (fromDistance > 0 || toDistance <= 0) {
			return null;
		}

		const movementProgress = fromDistance === 0 ? 0 : -fromDistance / (toDistance - fromDistance);

		const point = {
			x: from.x + (to.x - from.x) * movementProgress,
			y: from.y + (to.y - from.y) * movementProgress,
		};
		const normalX = -tangentY;
		const normalY = tangentX;
		// Dot the crossing offset with the normal to measure its distance from the checkpoint center.
		const distance = Math.abs((point.x - checkpoint.point.x) * normalX + (point.y - checkpoint.point.y) * normalY);

		if (distance > this.maxDistance) {
			return null;
		}

		return {
			checkpointIndex,
			distance,
			movementProgress,
			point,
			score: this.calculateScore(distance),
		};
	}

	private calculateScore(distance: number) {
		const { perfectDistance, imperfectBandWidth } = getScoreBandMetrics(this.maxDistance, this.scoreBandCount);

		if (distance <= perfectDistance) {
			return 1;
		}

		const imperfectBandCount = this.scoreBandCount - 1;
		const bandIndex = Math.min(
			imperfectBandCount,
			Math.ceil((distance - perfectDistance) / imperfectBandWidth - SCORE_BAND_BOUNDARY_EPSILON),
		);

		return 1 - bandIndex / this.scoreBandCount;
	}
}

export class CheckpointSequence {
	private activeStrokeIndex = 0;

	constructor(private readonly strokes: [StrokeEvaluator, ...StrokeEvaluator[]]) {}

	getActive() {
		return this.strokes[this.activeStrokeIndex];
	}

	getActiveIndex() {
		return this.activeStrokeIndex;
	}

	isFinalStroke() {
		return this.activeStrokeIndex === this.strokes.length - 1;
	}

	getScore() {
		return this.strokes.reduce((total, stroke) => total + stroke.getScore(), 0);
	}

	advance() {
		if (this.isFinalStroke()) {
			throw new Error('Cannot advance past the final tracing stroke');
		}

		this.activeStrokeIndex++;
	}

	reset() {
		this.activeStrokeIndex = 0;
		this.strokes.forEach((stroke) => stroke.reset());
	}
}
